import { useMemo, useState } from 'react';

import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Message } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import {
    CardNumberElement,
    useElements,
    useStripe
} from '@stripe/react-stripe-js';
import { FieldPath, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import * as yup from 'yup';

import { useRegisterDebitCard } from './useRegisterDebitCard';
import IwStripeProvider from '../../../../../../iwStripeProvider/IwStripeProvider';
import { getStripePublicKey } from '../../../../../../iwStripeProvider/StripeHelper';
import { BillingPostcodeField } from '../../../../../paymentDebitCard/StripeLinkDebitCard/formFields/BillingPostcodeField';
import { CardholderNameField } from '../../../../../paymentDebitCard/StripeLinkDebitCard/formFields/CardholderNameField';
import { CardNumberField } from '../../../../../paymentDebitCard/StripeLinkDebitCard/formFields/CardNumberField';
import { CVVField } from '../../../../../paymentDebitCard/StripeLinkDebitCard/formFields/CVVField';
import { ExpiryDateField } from '../../../../../paymentDebitCard/StripeLinkDebitCard/formFields/ExpiryDateField';
import { createLinkDebitCardSchema } from '../../../../../paymentDebitCard/StripeLinkDebitCard/formValidation/linkDebitCardSchema';
import type { TFormValues } from '../../../../../paymentDebitCard/StripeLinkDebitCard/StripeLinkDebitCardForm';
import { InnerLayout } from '../../InnerLayout/InnerLayout';
import { useRequirementsProgress } from '../../useRequirementsProgress';

const FUNDING_REQUIREMENTS_LIST_PATH = '/funding-requirements/';

export const DebitCardForm = () => {
    return (
        // @ts-expect-error redux-connected props (language) are injected, not passed
        <IwStripeProvider stripeKey={getStripePublicKey()}>
            <DebitCardFormContent />
        </IwStripeProvider>
    );
};

const DebitCardFormContent = () => {
    const intl = useIntl();
    const stripe = useStripe();
    const elements = useElements();
    const history = useHistory();
    const { search } = useLocation();
    const country = getCountry();
    const progress = useRequirementsProgress();

    const [invalidNumber, setInvalidNumber] = useState(false);
    const [invalidCardType, setInvalidCardType] = useState(false);
    const [invalidExpiryDate, setInvalidExpiryDate] = useState(false);
    const [invalidCVV, setInvalidCVV] = useState(false);

    const schema = useMemo(
        () => createLinkDebitCardSchema(intl, country),
        [intl, country]
    );

    const {
        register,
        getValues,
        setError,
        clearErrors,
        formState: { errors }
    } = useForm<TFormValues>({
        defaultValues: {
            billingPostcode: '',
            cardholderName: '',
            repaymentRole: true
        }
    });

    const {
        mutate: registerDebitCard,
        isLoading,
        isError,
        reset
    } = useRegisterDebitCard();

    const handleSubmit = () => {
        if (!stripe || !elements) return;

        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) return;

        const values = getValues();
        clearErrors();
        setInvalidCardType(false);

        try {
            schema.validateSync(values, { abortEarly: false });
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                for (const inner of err.inner) {
                    if (inner.path) {
                        setError(inner.path as FieldPath<TFormValues>, {
                            type: inner.type ?? 'validate',
                            message: inner.message
                        });
                    }
                }
            }
            return;
        }

        registerDebitCard(
            {
                stripe,
                cardNumberElement,
                billingPostcode: values.billingPostcode,
                cardholderName: values.cardholderName
            },
            {
                onSuccess: () => {
                    tracking.ampli.fundingRequirementsDebitCardCompleted({
                        version: '2.0'
                    });
                    tracking.ampli.fundingRequirementCompleted({
                        requirement: 'debit_card',
                        progress: { ...progress, debit_card: true },
                        version: '2.0'
                    });
                    history.push(
                        maintainStateKey({
                            pathname: FUNDING_REQUIREMENTS_LIST_PATH,
                            search
                        })
                    );
                },
                onError: (error) => {
                    if (
                        error instanceof Error &&
                        error.message === 'CARD_TYPE_NOT_DEBIT'
                    ) {
                        setInvalidCardType(true);
                    }
                }
            }
        );
    };

    return (
        <InnerLayout
            requirementType="DEBIT_CARD"
            title={
                <FormattedMessage
                    id="_.funding_requirements.debit_card_step.title"
                    defaultMessage="Add debit card for repayments"
                />
            }
            subheading={
                <FormattedMessage
                    id="_.funding_requirements.debit_card_step.subheading"
                    defaultMessage="Your scheduled repayments will come from this debit card."
                />
            }
        >
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    handleSubmit();
                }}
            >
                <CardholderNameField
                    formRegister={register}
                    error={errors.cardholderName?.message}
                    className="max-w-none"
                    placeholder="Name on card"
                />
                <CardNumberField
                    // The element is read at submit time via `elements.getElement`.
                    onReady={() => {}}
                    error={{ invalidNumber, invalidCardType }}
                    onChange={(event) => {
                        setInvalidNumber(Boolean(event.error));
                        // Clear both the local card-type flag and the mutation
                        // error so the generic banner can't reappear mid-edit.
                        if (invalidCardType) {
                            setInvalidCardType(false);
                            reset();
                        }
                    }}
                    className="max-w-none"
                    placeholder="0000 0000 0000 0000"
                />
                <div className="flex gap-l">
                    <div className="flex-1">
                        <ExpiryDateField
                            error={invalidExpiryDate}
                            onChange={(event) =>
                                setInvalidExpiryDate(Boolean(event.error))
                            }
                            className="max-w-none"
                            placeholder="MM / YY"
                        />
                    </div>
                    <div className="flex-1">
                        <CVVField
                            error={invalidCVV}
                            onChange={(event) =>
                                setInvalidCVV(Boolean(event.error))
                            }
                            className="max-w-none"
                            placeholder="CVC"
                            label={
                                <FormattedMessage
                                    id="_.funding_requirements.debit_card_step.security_code_label"
                                    defaultMessage="Security Code"
                                />
                            }
                        />
                    </div>
                </div>
                <BillingPostcodeField
                    formRegister={register}
                    error={errors.billingPostcode?.message}
                    className="max-w-none"
                    placeholder="SW1A 2AA"
                />

                {isError && !invalidCardType && (
                    <Message
                        className="mb-l"
                        type="error"
                        message={intl.formatMessage({
                            id: '_.funding_requirements.debit_card_step.error',
                            defaultMessage:
                                'Something went wrong adding your card. Please check your details and try again.'
                        })}
                    />
                )}

                <Button
                    variant="primary-rounded"
                    type="submit"
                    className="mt-l"
                    loading={isLoading}
                    disabled={!stripe || !elements}
                >
                    <FormattedMessage
                        id="_.funding_requirements.debit_card_step.confirm"
                        defaultMessage="Confirm card details"
                    />
                </Button>
            </form>
        </InnerLayout>
    );
};
